import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription
} from '../ui/alert-dialog';
import { Plus, ClipboardCheck, ArrowLeft, CheckCircle2, XCircle, Trash2, Search } from 'lucide-react';
import {
  apiCreateInventory, apiListInventories, apiGetInventory,
  apiUpdateInventoryLine, apiConfirmInventory, apiCancelInventory, apiDeleteInventory,
  apiListWarehouses
} from '../../erpApi';
import { toast } from 'sonner';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const STATUS_LABEL = { open: 'Aperto', confirmed: 'Confermato', cancelled: 'Annullato' };
const STATUS_COLOR = { open: 'bg-amber-100 text-amber-700', confirmed: 'bg-emerald-100 text-emerald-700', cancelled: 'bg-slate-100 text-slate-500' };

const NewInventoryDialog = ({ warehouses, onCreated, onClose }) => {
  const [f, setF] = useState({ warehouse_id: '', name: '', notes: '', include_zero_stock: false });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.warehouse_id) return toast.error('Seleziona magazzino');
    try {
      const inv = await apiCreateInventory(f);
      toast.success(`Inventario ${inv.number} creato`);
      onCreated(inv);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Nuovo inventario fisico</DialogTitle><DialogDescription>Genera uno snapshot delle giacenze attuali del magazzino. Potrai poi inserire le quantità reali contate.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="inv-form">
          <div className="space-y-1.5"><Label>Magazzino *</Label>
            <Select value={f.warehouse_id} onValueChange={(v) => setF({ ...f, warehouse_id: v })}>
              <SelectTrigger data-testid="inv-wh"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
              <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Nome / Descrizione</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="es. Inventario annuale 2026" /></div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          <div className="flex items-center justify-between p-2 rounded border border-slate-200">
            <div>
              <Label className="mb-0">Includi prodotti a giacenza 0</Label>
              <div className="text-xs text-slate-500">Utile se trovi articoli non a sistema</div>
            </div>
            <Switch checked={f.include_zero_stock} onCheckedChange={(v) => setF({ ...f, include_zero_stock: v })} />
          </div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Annulla</Button><Button data-testid="inv-submit" type="submit" className="bg-blue-700 hover:bg-blue-800">Crea sessione</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const InventoryDetail = ({ sessionId, onBack, onChanged }) => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [toConfirm, setToConfirm] = useState(false);
  const reload = async () => setData(await apiGetInventory(sessionId));
  useEffect(() => { reload(); }, [sessionId]); // eslint-disable-line

  const updateCount = async (line, value) => {
    if (value === '' || value === null || isNaN(parseFloat(value))) return;
    try {
      await apiUpdateInventoryLine(sessionId, line.line_id, { counted_quantity: parseFloat(value) });
      reload();
    } catch (e) { toast.error('Errore aggiornamento'); }
  };
  const confirm = async () => {
    try {
      const res = await apiConfirmInventory(sessionId);
      toast.success(`Confermato · ${res.generated_movements} movimenti generati · Δ valore €${res.value_delta}`);
      setToConfirm(false);
      reload(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const cancel = async () => {
    if (!window.confirm('Annullare la sessione? Nessuna giacenza sarà modificata.')) return;
    try { await apiCancelInventory(sessionId); toast.success('Sessione annullata'); reload(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  if (!data) return <div className="text-center py-12 text-sm text-slate-500">Caricamento...</div>;

  const isReadOnly = data.status !== 'open';
  const lines = (data.lines || []).filter(ln => {
    if (search) {
      const q = search.toLowerCase();
      if (!(ln.product_sku || '').toLowerCase().includes(q) && !(ln.product_name || '').toLowerCase().includes(q)) return false;
    }
    if (filter === 'counted' && ln.counted_quantity === null) return false;
    if (filter === 'uncounted' && ln.counted_quantity !== null) return false;
    if (filter === 'diff' && (ln.counted_quantity === null || (ln.difference || 0) === 0)) return false;
    return true;
  });
  const totalLines = data.lines?.length || 0;
  const counted = data.lines?.filter(ln => ln.counted_quantity !== null).length || 0;
  const withDiff = data.lines?.filter(ln => ln.counted_quantity !== null && (ln.difference || 0) !== 0).length || 0;
  const valueDelta = data.lines?.reduce((a, ln) => {
    if (ln.counted_quantity === null) return a;
    return a + ((ln.difference || 0) * (ln.avg_cost || 0));
  }, 0) || 0;

  return (
    <div className="space-y-3" data-testid="inv-detail">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5"><ArrowLeft className="w-4 h-4" />Elenco inventari</Button>
        <div className="flex items-center gap-2">
          {data.status === 'open' && (
            <>
              <Button variant="outline" size="sm" onClick={cancel} className="gap-1.5 text-slate-600"><XCircle className="w-3.5 h-3.5" />Annulla sessione</Button>
              <Button data-testid="inv-confirm-open" size="sm" className="bg-emerald-700 hover:bg-emerald-800 gap-1.5" onClick={() => setToConfirm(true)} disabled={counted === 0}>
                <CheckCircle2 className="w-3.5 h-3.5" />Conferma inventario ({withDiff} rettifiche)
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-sm font-semibold text-blue-700">{data.number}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLOR[data.status]}`}>{STATUS_LABEL[data.status]}</span>
            </div>
            <div className="text-base font-semibold">{data.name || 'Inventario senza nome'}</div>
            <div className="text-xs text-slate-500">
              Magazzino <span className="font-mono">{data.warehouse_code}</span> — {data.warehouse_name} ·
              Creato: {fmtDate(data.created_at)} da {data.created_by_email}
            </div>
            {data.confirmed_at && <div className="text-xs text-emerald-700 mt-1">Confermato: {fmtDate(data.confirmed_at)} da {data.confirmed_by_email}</div>}
          </div>
          <div className="grid grid-cols-4 gap-3 text-center">
            <div><div className="text-xl font-bold tabular-nums">{totalLines}</div><div className="text-[10px] uppercase text-slate-500">Righe</div></div>
            <div><div className="text-xl font-bold tabular-nums text-blue-700">{counted}</div><div className="text-[10px] uppercase text-slate-500">Contate</div></div>
            <div><div className="text-xl font-bold tabular-nums text-amber-700">{withDiff}</div><div className="text-[10px] uppercase text-slate-500">Diff.</div></div>
            <div><div className={`text-xl font-bold tabular-nums ${valueDelta < 0 ? 'text-red-700' : valueDelta > 0 ? 'text-emerald-700' : 'text-slate-700'}`}>{valueDelta >= 0 ? '+' : ''}€{valueDelta.toFixed(2)}</div><div className="text-[10px] uppercase text-slate-500">Δ Valore</div></div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="inv-search" placeholder="Cerca SKU o nome..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-8 w-[280px] text-sm" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-9 w-[180px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le righe</SelectItem>
            <SelectItem value="uncounted">Solo da contare</SelectItem>
            <SelectItem value="counted">Solo contate</SelectItem>
            <SelectItem value="diff">Solo con differenze</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500">{lines.length} di {totalLines}</div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">SKU</th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Prodotto</th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Ubic.</th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Lotto</th>
              <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Sistema</th>
              <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Contato</th>
              <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Δ</th>
              <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Costo medio</th>
              <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Δ Valore</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lines.map(ln => {
              const valueImpact = (ln.counted_quantity !== null) ? (ln.difference || 0) * (ln.avg_cost || 0) : null;
              return (
                <tr key={ln.line_id} className={`hover:bg-slate-50 ${ln.counted_quantity !== null && (ln.difference || 0) !== 0 ? 'bg-amber-50/30' : ''}`}>
                  <td className="px-3 py-2 font-mono text-xs">{ln.product_sku}</td>
                  <td className="px-3 py-2">{ln.product_name}</td>
                  <td className="px-3 py-2 text-xs font-mono">{ln.location_code || '—'}</td>
                  <td className="px-3 py-2 text-xs">{ln.lot_number || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{ln.system_quantity} {ln.unit_of_measure}</td>
                  <td className="px-3 py-2 text-right">
                    {isReadOnly ? (
                      <span className="font-medium tabular-nums">{ln.counted_quantity ?? '—'}</span>
                    ) : (
                      <input
                        data-testid={`inv-count-${ln.product_sku}`}
                        type="number" step="0.001" min="0"
                        defaultValue={ln.counted_quantity ?? ''}
                        onBlur={(e) => {
                          if (e.target.value === '' && ln.counted_quantity === null) return;
                          if (parseFloat(e.target.value) !== ln.counted_quantity) updateCount(ln, e.target.value);
                        }}
                        className="w-20 h-7 text-right text-xs tabular-nums border border-slate-200 rounded px-2 focus:border-blue-500 focus:outline-none"
                        placeholder="—"
                      />
                    )}
                  </td>
                  {(() => {
                    let diffColor = 'text-slate-700';
                    if (ln.counted_quantity === null) diffColor = 'text-slate-400';
                    else if (ln.difference > 0) diffColor = 'text-emerald-700';
                    else if (ln.difference < 0) diffColor = 'text-red-700';
                    let diffText = '—';
                    if (ln.counted_quantity !== null) diffText = (ln.difference > 0 ? '+' : '') + ln.difference;
                    return (
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${diffColor}`}>{diffText}</td>
                    );
                  })()}
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-500">€{(ln.avg_cost || 0).toFixed(3)}</td>
                  {(() => {
                    let valColor = 'text-slate-700';
                    if (valueImpact === null) valColor = 'text-slate-400';
                    else if (valueImpact < 0) valColor = 'text-red-700';
                    else if (valueImpact > 0) valColor = 'text-emerald-700';
                    let valText = '—';
                    if (valueImpact !== null) valText = (valueImpact >= 0 ? '+' : '') + '€' + valueImpact.toFixed(2);
                    return (
                      <td className={`px-3 py-2 text-right tabular-nums text-xs font-medium ${valColor}`}>{valText}</td>
                    );
                  })()}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AlertDialog open={toConfirm} onOpenChange={setToConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confermare l'inventario?</AlertDialogTitle>
            <AlertDialogDescription>
              Saranno generati automaticamente <strong>{withDiff}</strong> movimenti di rettifica (carico/scarico) per ripristinare le giacenze ai valori reali contati. L'operazione non è reversibile (puoi solo creare un nuovo inventario di rettifica).<br /><br />
              Impatto sul valore di magazzino: <strong className={valueDelta < 0 ? 'text-red-700' : 'text-emerald-700'}>{valueDelta >= 0 ? '+' : ''}€{valueDelta.toFixed(2)}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="inv-confirm-final" onClick={confirm} className="bg-emerald-700 hover:bg-emerald-800">Conferma e applica rettifiche</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const InventoryManager = () => {
  const [list, setList] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const reload = async () => {
    const [l, w] = await Promise.all([apiListInventories(), apiListWarehouses()]);
    setList(l); setWarehouses(w);
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line
  const del = async (s) => {
    if (!window.confirm(`Eliminare ${s.number}?`)) return;
    try { await apiDeleteInventory(s.id); toast.success('Eliminato'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  if (activeSession) {
    return <InventoryDetail sessionId={activeSession} onBack={() => { setActiveSession(null); reload(); }} onChanged={reload} />;
  }

  return (
    <div className="space-y-3" data-testid="inventory-manager">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-slate-500">Sessioni di inventario fisico — conteggio e riconciliazione massiva delle giacenze</div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button data-testid="inv-new" className="bg-blue-700 hover:bg-blue-800 gap-2 h-9"><Plus className="w-4 h-4" />Nuovo inventario</Button></DialogTrigger>
          {dialogOpen && <NewInventoryDialog
            warehouses={warehouses}
            onClose={() => setDialogOpen(false)}
            onCreated={(inv) => { setDialogOpen(false); reload(); setActiveSession(inv.id); }}
          />}
        </Dialog>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <ClipboardCheck className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <div className="text-sm font-medium text-slate-700">Nessun inventario fisico</div>
          <div className="text-xs text-slate-500 mt-1">Crea una sessione per fotografare le giacenze e contare</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Numero</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Nome</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Magazzino</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Righe</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Contate</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Differenze</th>
                <th className="text-center px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Stato</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Creato</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map(s => (
                <tr key={s.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`inv-row-${s.number}`} onClick={() => setActiveSession(s.id)}>
                  <td className="px-3 py-2 font-mono font-semibold text-blue-700">{s.number}</td>
                  <td className="px-3 py-2 text-slate-700">{s.name || '—'}</td>
                  <td className="px-3 py-2 text-xs"><span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{s.warehouse_code}</span> {s.warehouse_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.total_lines}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.counted_lines} / {s.total_lines}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-semibold ${s.lines_with_difference > 0 ? 'text-amber-700' : 'text-slate-500'}`}>{s.lines_with_difference}</td>
                  <td className="px-3 py-2 text-center"><span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLOR[s.status]}`}>{STATUS_LABEL[s.status]}</span></td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(s.created_at)}</td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    {s.status !== 'confirmed' && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => del(s)}><Trash2 className="w-3 h-3" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InventoryManager;
